/**
 * Add New Unit — light form aligned with AddNewConfiguration.
 * Sections: Basic Details, Pricing & Commission, Area, Key Highlights, Tags, Other Details.
 */
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Autocomplete,
  Chip,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InventoryKeyHighlighter from '../../../components/inventory/InventoryKeyHighlighter';
import InventoryOtherDetails from '../../../components/inventory/InventoryOtherDetails';
import InventoryAreaFields, { getAreaRowSlug } from '../../../components/inventory/InventoryAreaFields';
import {
  SECTION_COLORS,
  SectionHeader,
  AREA_UNIT_OPTIONS,
  CONFIG_TAG_SUGGESTIONS,
  UNIT_STATUS,
  formatPriceReadable,
  COMMISSION_RATE_INPUT_CONSTRAINTS,
  PRICE_INPUT_CONSTRAINTS,
  FACING_OPTIONS,
  UNIT_TAG_SUGGESTIONS,
} from '../../../utils/inventory_helper';
import { validateLightUnitPayload, buildUnitApiPayload } from '../components/unitCreation';

const DRAWER_WIDTH = 520;
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 12.5,
    height: 36,
    borderRadius: '6px',
    '& fieldset': { borderColor: SECTION_COLORS.border },
    '&:hover fieldset': { borderColor: '#B0B8C4' },
    '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12, top: -2 },
  '& .MuiInputLabel-shrink': { top: 0 },
  '& .MuiSelect-select': { fontSize: 12.5, paddingTop: 6, paddingBottom: 6 },
};


const FieldLabel = ({ label, required, complete, primary }) => {
  const labelColor = primary ? SECTION_COLORS.green : (complete ? SECTION_COLORS.green : SECTION_COLORS.text);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {complete && <CheckCircleOutlineIcon sx={{ fontSize: 13, color: SECTION_COLORS.green }} />}
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: labelColor, lineHeight: 1 }}>
        {label}{required && !complete && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
      </Typography>
    </Box>
  );
};

const AddNewUnit = ({
  open,
  onClose,
  onSubmit,
  config = {},
  project = {},
  projectId,
  configurationId,
  propertyTypes = [],
  uiSchema: uiSchemaProp = null,
  unitStatusOptions = [],
  facingOptions = [],
  areaUnitOptions = [],
}) => {
  const configName = config?.name ?? '';
  const projectName = project?.name ?? '';
  const areaUnit = config?.area_unit || 'sqft';
  const areaUnitOpts = areaUnitOptions?.length ? areaUnitOptions : AREA_UNIT_OPTIONS;
  const facingOpts = facingOptions?.length ? facingOptions : FACING_OPTIONS;
  const statusOpts = unitStatusOptions?.length ? unitStatusOptions : Object.entries(UNIT_STATUS).map(([k, v]) => ({ value: k, label: v.label }));

  const defaultCommission = config?.commission_rate ?? config?.commission ?? config?.metadata?.commission_rate;
  const defaultCommissionStr = defaultCommission != null && defaultCommission !== '' ? String(defaultCommission) : '';

  const [formData, setFormData] = useState({
    name: '',
    unitCode: '',
    floor: '',
    towerBlock: '',
    facing: '',
    status: 'available',
    basePrice: '',
    perSqftPrice: '',
    commissionRate: defaultCommissionStr,
    areaUnit: areaUnit || 'sqft',
    areaRows: [{ key: 'carpet_area', label: 'Carpet Area', value: '', unit: areaUnit || 'sqft' }],
    keyHighlights: [''],
    tags: [],
    otherDetails: [{ label: '', value: '' }],
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
    setSubmitError(null);
  };

  const resetForm = () => {
    const commissionDefault = config?.commission_rate ?? config?.commission ?? config?.metadata?.commission_rate;
    const commissionStr = commissionDefault != null && commissionDefault !== '' ? String(commissionDefault) : '';
    setFormData({
      name: '',
      unitCode: '',
      floor: '',
      towerBlock: '',
      facing: '',
      status: 'available',
      basePrice: '',
      perSqftPrice: '',
      commissionRate: commissionStr,
      areaUnit: areaUnit || 'sqft',
      areaRows: [{ key: 'carpet_area', label: 'Carpet Area', value: '', unit: areaUnit || 'sqft' }],
      keyHighlights: [''],
      tags: [],
      otherDetails: [{ label: '', value: '' }],
    });
    setErrors({});
    setSubmitError(null);
  };

  useEffect(() => {
    if (open) resetForm();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const payload = {
      name: formData.name?.trim(),
      unit_code: formData.unitCode?.trim(),
      tower_block: formData.towerBlock?.trim(),
      tower_wing: formData.towerBlock?.trim(),
      facing: formData.facing,
      base_price: formData.basePrice,
      area_rows: (formData.areaRows || []).map((row) => {
        const slug = getAreaRowSlug(row);
        const num = parseFloat(row.value);
        return slug && !Number.isNaN(num) && num >= 0 ? { key: slug, value: num, unit: row.unit || formData.areaUnit || 'sqft' } : null;
      }).filter(Boolean),
    };
    const { valid, errors: ve } = validateLightUnitPayload(payload, { requireArea: true });
    const next = {};
    if (ve.name) next.name = ve.name;
    if (ve.base_price) next.basePrice = ve.base_price;
    if (ve.area) next.area = ve.area;
    const slugs = (formData.areaRows || []).map((r) => getAreaRowSlug(r)).filter(Boolean);
    const duplicateSlug = slugs.length !== new Set(slugs).size;
    if (duplicateSlug) next.area = next.area || 'Each area type must be unique';
    setErrors(next);
    return valid && !duplicateSlug;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const seen = new Set();
      const area_rows = (formData.areaRows || [])
        .map((row) => {
          const slug = getAreaRowSlug(row);
          const num = parseFloat(row.value);
          if (!slug || Number.isNaN(num) || num < 0 || seen.has(slug)) return null;
          seen.add(slug);
          return { key: slug, value: num, unit: row.unit || formData.areaUnit || 'sqft' };
        })
        .filter(Boolean);
      const payload = {
        name: formData.name?.trim(),
        unit_code: formData.unitCode?.trim() || null,
        floor: formData.floor !== '' ? (Number(formData.floor) || null) : null,
        tower_block: formData.towerBlock?.trim(),
        tower_wing: formData.towerBlock?.trim(),
        facing: formData.facing || null,
        status: formData.status,
        base_price: formData.basePrice !== '' ? parseFloat(formData.basePrice) : null,
        per_sqft_price: formData.perSqftPrice !== '' ? formData.perSqftPrice : null,
        commission_rate: formData.commissionRate !== '' ? formData.commissionRate : null,
        area_rows,
        key_highlights: (formData.keyHighlights || []).filter((h) => (h ?? '').toString().trim()),
        tags: (formData.tags || []).map((t) => String(t).trim()).filter(Boolean),
        other_details: (formData.otherDetails || []).filter((r) => (r?.label ?? r?.value ?? '').toString().trim()),
      };
      const apiPayload = buildUnitApiPayload(payload, { areaUnit: formData.areaUnit });
      await onSubmit?.(apiPayload);
      onClose?.();
    } catch (err) {
      const data = err?.response?.data;
      const detail = typeof data?.detail === 'string' ? data.detail : Array.isArray(data?.detail) ? data.detail[0] : null;
      const message = detail || data?.message || err?.message || 'Unable to save unit. Please try again.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const basePriceNum = formData.basePrice !== '' && !Number.isNaN(parseFloat(formData.basePrice))
    ? parseFloat(formData.basePrice) : 0;
  const completePrice = basePriceNum > 0;
  const completeCommission = formData.commissionRate !== '' && !Number.isNaN(parseFloat(formData.commissionRate));
  const basePriceReadable = completePrice ? formatPriceReadable(basePriceNum) : null;

  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#F9FAFB',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${SECTION_COLORS.border}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: SECTION_COLORS.green, borderRadius: 1 }} />
              <Typography fontWeight={800} sx={{ color: SECTION_COLORS.text, letterSpacing: '-0.3px', fontSize: '1.05rem' }}>
                Add Unit
              </Typography>
            </Box>
            {(configName || projectName) && (
              <Typography sx={{ fontSize: 11.5, color: SECTION_COLORS.sub, pl: 1.5 }}>
                {[configName, projectName].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: SECTION_COLORS.sub }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {submitError && (
        <Box sx={{ px: 2.5, py: 1.5, flexShrink: 0, bgcolor: '#FEF2F2', borderBottom: `1px solid ${SECTION_COLORS.border}` }}>
          <Typography sx={{ fontSize: 12.5, color: '#B91C1C', fontWeight: 500 }}>
            {submitError}
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <form
          id="add-unit-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (validate()) handleSubmit();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {/* ─── Basic Details ───────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`, p: 2 }}>
            <SectionHeader title="Basic Details" accent="info" />
            <Grid container spacing={1.5}>

              <Grid item xs={12} sm={6}>
                <TextField
                  id="add-unit-name"
                  fullWidth
                  size="small"
                  label="Unit Name"
                  required
                  placeholder="e.g. A-301, B2-1204"
                  value={formData.name ?? ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true, htmlFor: 'add-unit-name' }}
                  inputProps={{ id: 'add-unit-name' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  id="add-unit-code"
                  fullWidth
                  size="small"
                  label="Unit Code"
                  placeholder="Optional code"
                  value={formData.unitCode ?? ''}
                  onChange={(e) => handleChange('unitCode', e.target.value)}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true, htmlFor: 'add-unit-code' }}
                  inputProps={{ id: 'add-unit-code' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  id="add-unit-floor"
                  fullWidth
                  size="small"
                  label="Floor (optional)"
                  type="number"
                  placeholder="e.g. 2 — leave blank for plot/farmland"
                  value={formData.floor ?? ''}
                  onChange={(e) => handleChange('floor', e.target.value)}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true, htmlFor: 'add-unit-floor' }}
                  inputProps={{ id: 'add-unit-floor', min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  id="add-unit-tower"
                  fullWidth
                  size="small"
                  label="Tower/Block (optional)"
                  placeholder="e.g. Tower A, Block B — leave blank for plot/farmland"
                  value={formData.towerBlock ?? ''}
                  onChange={(e) => handleChange('towerBlock', e.target.value)}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true, htmlFor: 'add-unit-tower' }}
                  inputProps={{ id: 'add-unit-tower' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* Use a div-based label instead of <label> to avoid htmlFor click hijacking */}
                <Box sx={{ position: 'relative' }}>
                  <Typography
                    component="span"
                    sx={{
                      position: 'absolute',
                      top: -9,
                      left: 10,
                      zIndex: 1,
                      fontSize: 11,
                      px: 0.5,
                      bgcolor: '#fff',
                      color: SECTION_COLORS.sub,
                      lineHeight: 1,
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    Facing (optional)
                  </Typography>
                  <Select
                    value={formData.facing ?? ''}
                    onChange={(e) => handleChange('facing', e.target.value)}
                    displayEmpty
                    size="small"
                    fullWidth
                    inputProps={{ id: 'add-unit-facing-input' }}
                    MenuProps={{ disablePortal: false, disableScrollLock: true }}
                    sx={{
                      fontSize: 12.5,
                      height: 36,
                      borderRadius: '6px',
                      '& fieldset': { borderColor: SECTION_COLORS.border },
                      '&:hover fieldset': { borderColor: '#B0B8C4' },
                      '&.Mui-focused fieldset': { borderColor: '#2D6A4F', borderWidth: 1.5 },
                      '& .MuiSelect-select': { fontSize: 12.5, py: '6px' },
                    }}
                    renderValue={(v) => {
                      if (v == null || v === '') return <span style={{ color: '#9CA3AF', fontSize: 12.5 }}>Select</span>;
                      const opt = (facingOpts || []).find((o) => o.value === v);
                      return opt ? (opt.label ?? opt.value) : v;
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12.5 }}>Select</MenuItem>
                    {(facingOpts || []).map((o) => (
                      <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12.5 }}>
                        {o.label ?? o.value}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Grid>

            </Grid>
          </Box>

          {/* ─── Price & Commission ─────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`, p: 2 }}>
            <SectionHeader
              title="Price & Commission"
              subtitle="Base price and commission rate"
              accent="pricing"
              complete={completePrice && completeCommission}
            />
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 1.5 }}>
                <FieldLabel label="Base Price" required complete={completePrice} primary />
                <Box sx={{ border: `1px solid ${errors.basePrice ? '#DC2626' : `${SECTION_COLORS.green}28`}`, borderRadius: '8px', p: 1.5, bgcolor: SECTION_COLORS.greenLight }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="e.g. 7500000"
                    value={formData.basePrice ?? ''}
                    onChange={(e) => handleChange('basePrice', e.target.value)}
                    error={!!errors.basePrice}
                    sx={fieldSx}
                    inputProps={PRICE_INPUT_CONSTRAINTS}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontSize: 12, color: SECTION_COLORS.sub }}>₹</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: basePriceReadable ? (
                        <InputAdornment position="end">
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: SECTION_COLORS.green }}>
                            {basePriceReadable}
                          </Typography>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Box>
                {errors.basePrice && <Typography sx={{ fontSize: 11, color: '#DC2626', mt: 0.5 }}>{errors.basePrice}</Typography>}
              </Box>
              <Box sx={{ mb: 0 }}>
                <FieldLabel label="Commission Rate" required complete={completeCommission} primary />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={formData.commissionRate ?? ''}
                    onChange={(e) => handleChange('commissionRate', e.target.value)}
                    error={!!errors.commissionRate}
                    helperText={errors.commissionRate}
                    placeholder="e.g. 5"
                    sx={{ ...fieldSx, width: 160 }}
                    inputProps={COMMISSION_RATE_INPUT_CONSTRAINTS}
                    InputProps={{
                      endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 13, color: SECTION_COLORS.sub }}>%</Typography></InputAdornment>,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ─── Area ──────────────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`, p: 2 }}>
            {errors.area && (
              <Typography sx={{ fontSize: 11, color: '#dc2626', mb: 1 }}>{errors.area}</Typography>
            )}
            <InventoryAreaFields
              value={formData.areaRows}
              onChange={(v) => handleChange('areaRows', v)}
              areaUnit={formData.areaUnit}
              areaUnitOptions={areaUnitOpts}
              accent="info"
            />
          </Box>

          {/* ─── Key Highlights ─────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`, p: 2 }}>
            <SectionHeader title="Key highlights" accent="highlights" />
            <InventoryKeyHighlighter
              value={Array.isArray(formData.keyHighlights) && formData.keyHighlights.length > 0 ? formData.keyHighlights : ['']}
              onChange={(v) => handleChange('keyHighlights', v)}
              placeholder="e.g. Sea facing, Corner unit"
            />
          </Box>

          {/* ─── Tags ──────────────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`, p: 2 }}>
            <SectionHeader title="Tags" accent="tags" />
            <Autocomplete
              multiple
              freeSolo
              options={UNIT_TAG_SUGGESTIONS.concat(CONFIG_TAG_SUGGESTIONS || []).filter((v, i, a) => a.indexOf(v) === i)}
              value={formData.tags || []}
              onChange={(_, v) => handleChange('tags', v || [])}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o?.label ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Type or pick a tag"
                  sx={fieldSx}
                />
              )}
              renderTags={(vals, getTagProps) =>
                vals.map((opt, idx) => (
                  <Chip
                    {...getTagProps({ index: idx })}
                    key={opt}
                    label={opt}
                    size="small"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(0,105,92,0.10)', color: SECTION_COLORS.teal, border: 'none' }}
                  />
                ))
              }
            />
          </Box>

          {/* ─── Other Details ───────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${SECTION_COLORS.border}`, p: 2 }}>
            <InventoryOtherDetails
              value={Array.isArray(formData.otherDetails) && formData.otherDetails.length > 0 ? formData.otherDetails : [{ label: '', value: '' }]}
              onChange={(v) => handleChange('otherDetails', v)}
              title="Other details"
              subtitle="Add any label and value"
              accent="other"
            />
          </Box>
        </form>
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, flexShrink: 0, borderTop: `1px solid ${SECTION_COLORS.border}`, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          type="button"
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#6B7280', border: '1.5px solid #E4E7EC', borderRadius: '5px', px: '14px', height: 30 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-unit-form"
          variant="contained"
          disabled={submitting}
          onClick={(e) => { e.preventDefault(); if (validate()) handleSubmit(); }}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: SECTION_COLORS.green, border: 'none', borderRadius: '5px', px: '14px', height: 30, boxShadow: 'none', '&:hover': { bgcolor: '#1b5e20', boxShadow: 'none' }, '&:disabled': { opacity: 0.6 } }}
        >
          {submitting ? 'Saving…' : 'Save Unit'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddNewUnit;
